'use client';
import * as React from 'react';
import { Track } from 'livekit-client';
import {
  useMaybeLayoutContext,
  MediaDeviceMenu,
  TrackToggle,
  useRoomContext,
  useIsRecording,
} from '@livekit/components-react';
import { CameraSettings } from './CameraSettings';
import { MicrophoneSettings } from './MicrophoneSettings';
import { Settings, Video, Mic, Headphones, CircleDot } from 'lucide-react';
import { useHostSettings } from './HostSettingsContext';
import { useHostControl } from './HostControlContext';
import { playToastAlertSound } from './notificationSound';
import { publishRecordingEvent, recordingActionToWindowEvent } from './recordingEvents';
import {
  fetchRecordingStatus,
  isAwaitingFileRecordingState,
  isConfirmedFileRecording,
  startRecordingFromRoom,
} from './recording-status-client';
import { useFileRecordingActive } from './useFileRecordingActive';
import toast from 'react-hot-toast';

/**
 * @alpha
 */
export interface SettingsMenuProps extends React.HTMLAttributes<HTMLDivElement> {}

/**
 * @alpha
 */
export function SettingsMenu(props: SettingsMenuProps) {
  const layoutContext = useMaybeLayoutContext();
  const room = useRoomContext();
  const { isHost } = useHostSettings();
  const { isCoHost } = useHostControl();
  const recordingEndpoint = process.env.NEXT_PUBLIC_LK_RECORD_ENDPOINT || '/api/record';

  const notifyRecordingAction = React.useCallback(
    (
      action: 'preparing' | 'starting' | 'started' | 'stopped' | 'failed' | 'cancelled',
      actorName?: string,
      opts?: { queuePosition?: number },
    ) => {
      const name = actorName || room.localParticipant?.name || 'Alguém';
      const messages: Record<string, string> = {
        preparing: `${name} está preparando a gravação…`,
        starting: `${name} está iniciando a gravação…`,
        started: `${name} iniciou a gravação.`,
        stopped: `${name} parou a gravação.`,
        failed: 'Não foi possível iniciar a gravação.',
        cancelled: 'Preparação da gravação cancelada.',
      };
      const message = messages[action] ?? messages.started;
      const icon =
        action === 'stopped' || action === 'cancelled'
          ? '⏹️'
          : action === 'failed'
            ? '⚠️'
            : action === 'preparing'
              ? '⏳'
              : '⏺️';
      toast(message, { icon });
    },
    [room],
  );

  const pollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = React.useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = React.useCallback(
    (roomName: string) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const status = await fetchRecordingStatus(roomName);
          if (isConfirmedFileRecording(status)) {
            window.dispatchEvent(new CustomEvent('recording:start'));
            stopPolling();
            setProcessingRecRequest(false);
          } else if (status.state === 'idle' || status.state === 'failed' || status.state === 'cancelled') {
            window.dispatchEvent(new CustomEvent('recording:stop'));
            stopPolling();
            setProcessingRecRequest(false);
          } else if (isAwaitingFileRecordingState(status.state)) {
            window.dispatchEvent(
              new CustomEvent(
                status.state === 'starting' ? 'recording:starting' : 'recording:preparing',
              ),
            );
          }
        } catch {
          /* ignore transient poll errors */
        }
      }, 2000);
    },
    [stopPolling],
  );

  React.useEffect(() => () => stopPolling(), [stopPolling]);

  const settings = React.useMemo(() => {
    return {
      media: { camera: true, microphone: true, label: 'Media Devices', speaker: true },
      recording: recordingEndpoint ? { label: 'Recording' } : undefined,
    };
  }, []);

  const tabs = React.useMemo(
    () => Object.keys(settings).filter((t) => t !== undefined) as Array<keyof typeof settings>,
    [settings],
  );
  const [activeTab, setActiveTab] = React.useState(tabs[0]);

  const isLiveKitRecording = useIsRecording();
  const isFileRecording = useFileRecordingActive(room.name);
  const isRecording = isLiveKitRecording || isFileRecording;
  const canControlRecording = isHost || isCoHost;
  const isStartDisabled = !canControlRecording && !isRecording;
  const [initialRecStatus, setInitialRecStatus] = React.useState(isRecording);
  const [processingRecRequest, setProcessingRecRequest] = React.useState(false);

  React.useEffect(() => {
    if (initialRecStatus !== isRecording) {
      setProcessingRecRequest(false);
    }
  }, [isRecording, initialRecStatus]);

  const toggleRoomRecording = async () => {
    if (!recordingEndpoint) {
      throw TypeError('No recording endpoint specified');
    }
    if (room.isE2EEEnabled) {
      throw Error('Recording of encrypted meetings is currently not supported');
    }
    if (!isRecording && !canControlRecording) {
      playToastAlertSound();
      toast.error('Somente anfitriões ou co-anfitriões podem iniciar uma gravação.');
      return;
    }
    if (isRecording && !canControlRecording) {
      playToastAlertSound();
      toast.error('Somente anfitriões ou co-anfitriões podem parar a gravação.');
      return;
    }
    setProcessingRecRequest(true);
    setInitialRecStatus(isRecording);
    try {
      if (isRecording) {
        const response = await fetch(recordingEndpoint + `/stop?roomName=${encodeURIComponent(room.name)}`);
        if (response.ok || response.status === 404) {
          notifyRecordingAction('stopped');
          publishRecordingEvent(room, 'stopped');
          window.dispatchEvent(new CustomEvent('recording:stop'));
          stopPolling();
        } else {
          console.error('Error stopping recording:', response.status, response.statusText);
        }
        setProcessingRecRequest(false);
        return;
      }

      const result = await startRecordingFromRoom(room);

      if (result.httpStatus === 202 || result.state === 'preparing') {
        notifyRecordingAction('preparing', room.localParticipant.name || 'Alguém', {
          queuePosition: result.queuePosition,
        });
        publishRecordingEvent(room, 'preparing', { queuePosition: result.queuePosition });
        window.dispatchEvent(new CustomEvent('recording:preparing'));
        startPolling(room.name);
        return;
      }

      if (result.httpStatus >= 400) {
        notifyRecordingAction('failed');
        setProcessingRecRequest(false);
        return;
      }

      const action: 'starting' | 'started' = result.state === 'active' ? 'started' : 'starting';
      notifyRecordingAction(action);
      publishRecordingEvent(room, action);
      window.dispatchEvent(new CustomEvent(recordingActionToWindowEvent(action)));
      startPolling(room.name);
    } catch (error) {
      console.error('Error handling recording request:', error);
      setProcessingRecRequest(false);
    }
  };

  const getTabIcon = (tab: string) => {
    switch (tab) {
      case 'media':
        return <Settings className="w-4 h-4" />;
      case 'recording':
        return <CircleDot className="w-4 h-4" />;
      default:
        return null;
    }
  };

  return (
    <div
      className="settings-menu"
      style={{
        width: '100%',
        background: 'var(--lk-bg2)',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
      {...props}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '600',
            color: 'var(--lk-fg)',
          }}
        >
          Settings
        </h2>
        <button
          className="lk-button"
          onClick={() => layoutContext?.widget.dispatch?.({ msg: 'toggle_settings' })}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'var(--lk-fg)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          Close
        </button>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          paddingBottom: '16px',
        }}
      >
        {tabs.map(
          (tab) =>
            settings[tab] && (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: tab === activeTab ? 'var(--lk-accent-bg)' : 'transparent',
                  color: tab === activeTab ? 'var(--lk-accent-fg)' : 'var(--lk-fg)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                {getTabIcon(tab)}
                {
                  // @ts-ignore
                  settings[tab].label
                }
              </button>
            ),
        )}
      </div>

      {/* Content */}
      <div style={{ minHeight: '400px' }}>
        {activeTab === 'media' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {settings.media && settings.media.camera && (
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  padding: '20px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px',
                  }}
                >
                  <Video className="w-5 h-5" style={{ color: 'var(--lk-accent-bg)' }} />
                  <h3
                    style={{
                      margin: 0,
                      fontSize: '16px',
                      fontWeight: '600',
                      color: 'var(--lk-fg)',
                    }}
                  >
                    Camera
                  </h3>
                </div>
                <CameraSettings />
              </div>
            )}

            {settings.media && settings.media.microphone && (
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  padding: '20px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px',
                  }}
                >
                  <Mic className="w-5 h-5" style={{ color: 'var(--lk-accent-bg)' }} />
                  <h3
                    style={{
                      margin: 0,
                      fontSize: '16px',
                      fontWeight: '600',
                      color: 'var(--lk-fg)',
                    }}
                  >
                    Microphone
                  </h3>
                </div>
                <MicrophoneSettings />
              </div>
            )}

            {settings.media && settings.media.speaker && (
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  padding: '20px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px',
                  }}
                >
                  <Headphones className="w-5 h-5" style={{ color: 'var(--lk-accent-bg)' }} />
                  <h3
                    style={{
                      margin: 0,
                      fontSize: '16px',
                      fontWeight: '600',
                      color: 'var(--lk-fg)',
                    }}
                  >
                    Speaker & Headphones
                  </h3>
                </div>
                <section className="lk-button-group" style={{ display: 'flex', gap: '8px' }}>
                  <span
                    className="lk-button"
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: 'var(--lk-fg)',
                    }}
                  >
                    Audio Output
                  </span>
                  <div className="lk-button-group-menu">
                    <MediaDeviceMenu kind="audiooutput" />
                  </div>
                </section>
              </div>
            )}
          </div>
        )}

        {activeTab === 'recording' && (
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              padding: '20px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px',
              }}
            >
              <CircleDot className="w-5 h-5" style={{ color: 'var(--lk-accent-bg)' }} />
              <h3
                style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--lk-fg)',
                }}
              >
                Record Meeting
              </h3>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p
                style={{
                  margin: 0,
                  color: 'var(--lk-fg)',
                  opacity: 0.8,
                  fontSize: '14px',
                }}
              >
                {isRecording
                  ? 'Meeting is currently being recorded'
                  : 'No active recordings for this meeting'}
              </p>
            </div>

            <button
              disabled={processingRecRequest || isStartDisabled}
              onClick={() => toggleRoomRecording()}
              aria-disabled={isStartDisabled}
              data-start-disabled={isStartDisabled ? 'true' : undefined}
              title={isStartDisabled ? 'Somente o anfitrião pode iniciar uma gravação' : undefined}
              style={{
                padding: '12px 24px',
                borderRadius: '6px',
                border: 'none',
                background: isRecording ? 'var(--lk-danger3)' : 'var(--lk-accent-bg)',
                color: isRecording ? 'white' : 'var(--lk-accent-fg)',
                cursor: processingRecRequest || isStartDisabled ? 'not-allowed' : 'pointer',
                opacity: processingRecRequest || isStartDisabled ? 0.6 : 1,
                transition: 'all 0.2s ease',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              {processingRecRequest
                ? 'Processing...'
                : isRecording
                  ? 'Stop Recording'
                  : 'Start Recording'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
